/**
 * Plurales de los diccionarios: cada texto con número tiene un par
 * `xxxOne` / `xxxOther` junto a la clave original, y aquí se elige cuál
 * usar con las reglas CLDR del idioma (Intl.PluralRules).
 *
 * Solo distinguimos `one` y `other`, que es lo que necesitan es, en, pt, de
 * y fr. Cualquier otra categoría (p. ej. `many` en francés para millones)
 * cae en `other`. El francés trata el 0 como singular («0 tâche»), y eso
 * lo resuelve Intl, no este archivo.
 */

export interface PluralForms {
  one: string;
  other: string;
}

const reglas = new Map<string, Intl.PluralRules | null>();

function reglasDe(locale: string): Intl.PluralRules | null {
  if (!reglas.has(locale)) {
    let r: Intl.PluralRules | null = null;
    try {
      r = new Intl.PluralRules(locale);
    } catch {
      // Locale mal formado: se usa la regla del inglés.
    }
    reglas.set(locale, r);
  }
  return reglas.get(locale) ?? null;
}

export function pluralCategory(locale: string, n: number): 'one' | 'other' {
  const r = reglasDe(locale);
  if (!r) return n === 1 ? 'one' : 'other';
  return r.select(n) === 'one' ? 'one' : 'other';
}

/**
 * Devuelve la forma adecuada para `n` con `{n}` (y las variables extra)
 * sustituidas. Si falta una de las dos formas, usa la otra: nunca devuelve
 * vacío por una clave ausente.
 */
export function plural(
  locale: string,
  n: number,
  forms: Partial<PluralForms>,
  vars: Record<string, string | number> = {},
): string {
  const cat = pluralCategory(locale, n);
  const plantilla = (cat === 'one' ? forms.one ?? forms.other : forms.other ?? forms.one) ?? '';
  const todas: Record<string, string | number> = { n, ...vars };
  return plantilla.replace(/\{(\w+)\}/g, (m, k: string) => (k in todas ? String(todas[k]) : m));
}
